import { toast } from "@/hooks/useToast";

interface DevErrorToastProps {
  error: unknown;
  context?: string;
}

/**
 * Componente per mostrare errori tecnici solo in development
 * @param error - L'errore da mostrare
 * @param context - Contesto opzionale per identificare la fonte dell'errore
 * 
 * @example
 * // Uso semplice
 * showDevError({ error: supabaseError });
 * 
 * // Con contesto
 * showDevError({ error: supabaseError, context: 'user login' });
 * 
 * // Nel catch
 * } catch (error) {
 *   showDevError({ error, context: 'database operation' });
 * }
 * 
 * // Output formattato per oggetti Supabase:
 * // Code: PGRST200
 * // Message: Could not find a relationship between 'users' and 'user_roles' (obsolete)
 * // Details: Searched for a foreign key relationship...
 * // Hint: null
 */
export const showDevError = ({ error, context }: DevErrorToastProps) => {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  // Funzione per formattare l'errore
  const formatError = (error: unknown): string => {
    if (error instanceof Error) {
      return error.message;
    }
    
    if (typeof error === 'object' && error !== null) {
      // Se è un oggetto Supabase con proprietà specifiche
      const errorObj = error as Record<string, unknown>;
      if (errorObj.message || errorObj.details || errorObj.code) {
        let formatted = '';
        
        if (errorObj.code) {
          formatted += `Code: ${errorObj.code}\n`;
        }
        if (errorObj.message) {
          formatted += `Message: ${errorObj.message}\n`;
        }
        if (errorObj.details) {
          formatted += `Details: ${errorObj.details}\n`;
        }
        if (errorObj.hint) {
          formatted += `Hint: ${errorObj.hint}\n`;
        }
        
        return formatted.trim();
      }
      
      // Per altri oggetti, prova a fare JSON.stringify
      try {
        return JSON.stringify(error, null, 2);
      } catch {
        return String(error);
      }
    }
    
    return String(error);
  };

  const errorMessage = formatError(error);
  const contextPrefix = context ? `[${context}] ` : '';

  toast({
    title: "Errore Tecnico (Dev)",
    description: `${contextPrefix}${errorMessage}`,
    variant: "destructive",
  });
};

/**
 * Hook per gestire errori con toast automatico in development
 * 
 * @example
 * const { handleError } = useDevError();
 * 
 * // Uso diretto
 * handleError(error, 'user profile update');
 * 
 * // Nel catch
 * } catch (error) {
 *   handleError(error, 'database operation');
 * }
 */
export const useDevError = () => {
  const handleError = (error: unknown, context?: string) => {
    console.error(`Error${context ? ` in ${context}` : ''}:`, error);
    showDevError({ error, context });
  };

  return { handleError, showDevError };
}; 